import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DoneIcon from '@mui/icons-material/Done';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { generateListPageReportURL } from '../../../utils/report';

/** Characters of the id shown before it is ellipsed. The full value is copied. */
export const REPORT_ID_DISPLAY_LENGTH = 10;

export const truncateReportId = (id: string): string =>
  id.length > REPORT_ID_DISPLAY_LENGTH ? `${id.slice(0, REPORT_ID_DISPLAY_LENGTH)}…` : id;

export interface ReportIdCellProps {
  /** The report's unique code — quoted between users to identify a report. */
  reportId: string;
  /** The report's type key (e.g. 'ServiceList'), which decides the link's page. */
  reportType: string;
}

/**
 * An absolute link that opens the report on its own list page.
 *
 * `window.location.origin` rather than a hardcoded host, so the link a user
 * copies on dev points at dev and the one they copy on prod points at prod. The
 * app mounts a BrowserRouter at the root with no basename, so the origin plus
 * the route path is the whole URL.
 */
export const buildReportShareUrl = (reportType: string, reportId: string): string =>
  // Encoded: the id lands in a query value, so a reserved character (`&`, `#`,
  // a space) would otherwise truncate or corrupt the link it is pasted into.
  `${window.location.origin}${generateListPageReportURL(reportType, encodeURIComponent(reportId))}`;

const FEEDBACK_MS = 1500;

/**
 * The report's id, shortened to stay inside the column, with a copy button that
 * writes a full link to the report — the point of the column is to hand someone
 * else something they can act on, so it copies the whole URL rather than the
 * shortened text on screen.
 */
export const ReportIdCell: React.FC<ReportIdCellProps> = ({ reportId, reportType }) => {
  const [copied, setCopied] = useState(false);
  const [errored, setErrored] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearFeedbackTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => clearFeedbackTimer, [clearFeedbackTimer]);

  const handleCopy = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();
      try {
        await navigator.clipboard.writeText(buildReportShareUrl(reportType, reportId));
        setCopied(true);
        setErrored(false);
      } catch {
        // Clipboard access is refused on insecure origins and by permission.
        setCopied(false);
        setErrored(true);
      }
      clearFeedbackTimer();
      timerRef.current = setTimeout(() => {
        setCopied(false);
        setErrored(false);
      }, FEEDBACK_MS);
    },
    [reportId, reportType, clearFeedbackTimer]
  );

  if (!reportId) return null;

  const isTruncated = reportId.length > REPORT_ID_DISPLAY_LENGTH;
  const tooltip = errored
    ? 'Could not access clipboard'
    : copied
      ? 'Copied link to this report'
      : `Copy a link to report ${reportId}`;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
      <Box
        component="span"
        // The full id on hover, so it can be read without copying.
        title={isTruncated ? reportId : undefined}
        sx={{
          fontFamily: 'monospace',
          fontSize: '11px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {truncateReportId(reportId)}
      </Box>
      <Tooltip title={tooltip} placement="top">
        <IconButton
          size="small"
          aria-label={`Copy a link to report ${reportId}`}
          onClick={handleCopy}
          sx={{ p: 0.25, color: copied ? 'success.main' : 'text.secondary' }}
        >
          {copied ? <DoneIcon sx={{ fontSize: 13 }} /> : <ContentCopyIcon sx={{ fontSize: 13 }} />}
        </IconButton>
      </Tooltip>
    </Box>
  );
};
